import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ChevronIcon } from 'assets/icons/chevron.svg';
import { clsx } from 'clsx';
import dayjs from 'dayjs';

import { isHighImpactRegulation } from '../utils/monitoringResultsUtils';

import type IMonitoringRegulationCard from './interfaces/IMonitoringRegulationCard';

import styles from './sass/MonitoringRegulationCard.module.scss';

const formatOptionalDate = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }

  return dayjs(value).isValid() ? dayjs(value).format('MMM D, YYYY') : value;
};

function MonitoringRegulationCard({
  regulation,
  defaultExpanded = isHighImpactRegulation(regulation),
}: IMonitoringRegulationCard): React.JSX.Element {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);

  const enactedDate = formatOptionalDate(regulation.enacted_date);
  const effectiveDate = formatOptionalDate(regulation.effective_date);
  const expectedEnforcementDate = formatOptionalDate(regulation.expected_enforcement_date);

  return (
    <article
      className={clsx(styles.container, {
        [styles.impactCritical]: regulation.impact_level === 'critical',
        [styles.impactHigh]: regulation.impact_level === 'high',
        [styles.impactMedium]: regulation.impact_level === 'medium',
        [styles.impactLow]: regulation.impact_level === 'low',
        [styles.rumor]: regulation.approval_state === 'rumor',
      })}
    >
      <button
        type="button"
        className={styles.header}
        onClick={() => setIsExpanded((prevExpanded) => !prevExpanded)}
        aria-expanded={isExpanded}
      >
        <div className={styles.headerMain}>
          <h3 className={styles.title}>{regulation.regulation_name}</h3>
          <span className={styles.productName}>{regulation.product_name}</span>

          <div className={styles.metaRow}>
            <span>{regulation.regulatory_body}</span>
            <span aria-hidden="true">·</span>
            <span>{regulation.jurisdiction}</span>
          </div>

          <div className={styles.badgeRow}>
            <span className={clsx(
              styles.badge,
              regulation.source_type === 'official' ? styles.sourceOfficial : styles.sourceOther,
            )}
            >
              {t(`valueChainMap.monitoring.results.sourceType.${regulation.source_type}`)}
            </span>
            <span className={clsx(styles.badge, styles[`approval_${regulation.approval_state}`])}>
              {t(`valueChainMap.monitoring.results.approvalState.${regulation.approval_state}`)}
            </span>
            <span className={clsx(styles.badge, styles[`impact_${regulation.impact_level}`])}>
              {t(`valueChainMap.monitoring.results.impactLevel.${regulation.impact_level}`)}
            </span>
            {typeof regulation.confidence === 'number' ? (
              <span className={styles.confidence}>
                {t('valueChainMap.monitoring.results.confidence', {
                  value: Math.round(regulation.confidence * 100),
                })}
              </span>
            ) : null}
          </div>
        </div>

        <ChevronIcon
          className={clsx(styles.chevron, { [styles.chevronExpanded]: isExpanded })}
          aria-hidden="true"
        />
      </button>

      {isExpanded ? (
        <div className={styles.body}>
          {regulation.impact_summary ? (
            <p className={styles.summary}>{regulation.impact_summary}</p>
          ) : null}

          <div className={styles.detailGrid}>
            {regulation.factory_location?.label ? (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>
                  {t('valueChainMap.monitoring.results.factoryLocation')}
                </span>
                <span className={styles.detailValue}>{regulation.factory_location.label}</span>
              </div>
            ) : null}

            {regulation.related_materials?.length ? (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>
                  {t('valueChainMap.monitoring.results.relatedMaterials')}
                </span>
                <span className={styles.detailValue}>
                  {regulation.related_materials.join(', ')}
                </span>
              </div>
            ) : null}

            {enactedDate ? (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>
                  {t('valueChainMap.monitoring.results.enactedDate')}
                </span>
                <span className={styles.detailValue}>{enactedDate}</span>
              </div>
            ) : null}

            {effectiveDate ? (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>
                  {t('valueChainMap.monitoring.results.effectiveDate')}
                </span>
                <span className={styles.detailValue}>{effectiveDate}</span>
              </div>
            ) : null}

            {expectedEnforcementDate ? (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>
                  {t('valueChainMap.monitoring.results.expectedEnforcementDate')}
                </span>
                <span className={styles.detailValue}>{expectedEnforcementDate}</span>
              </div>
            ) : null}

            {regulation.source_links?.length ? (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>
                  {t('valueChainMap.monitoring.results.sources')}
                </span>
                <div className={styles.sourceList}>
                  {regulation.source_links.map((sourceLink) => (
                    <a
                      key={`${regulation.id}-${sourceLink.url}`}
                      href={sourceLink.url}
                      className={styles.sourceLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {sourceLink.label}
                    </a>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          {regulation.recommended_action ? (
            <p className={styles.recommendedAction}>
              {regulation.recommended_action}
            </p>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

export default MonitoringRegulationCard;
